import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiShoppingCart, FiUser, FiSun, FiMoon } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';
import api from '../../utils/api';
import BrandLogo from '../common/BrandLogo';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState('/logo.png');
  const { isAuthenticated, user, logout, isAdmin } = useAuth();
  const { getCartCount } = useCart();
  const { darkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const cartCount = getCartCount();

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await api.get('/customizations/logo');
        if (res.data.success && res.data.logoUrl) {
          setLogoUrl(res.data.logoUrl);
        }
      } catch (err) {
        // Fallback to default
      }
    };
    fetchLogo();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 bg-white dark:bg-gray-900 shadow-md">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <BrandLogo />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-primary-light dark:hover:text-primary-dark transition">
              Home
            </Link>
            <Link to="/products" className="hover:text-primary-light dark:hover:text-primary-dark transition">
              Products
            </Link>
            <Link to="/about" className="hover:text-primary-light dark:hover:text-primary-dark transition">
              About
            </Link>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-2 md:space-x-4">
            {/* Theme Toggle - Always Visible */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              aria-label="Toggle theme"
            >
              {darkMode ? (
                <FiSun className="w-5 h-5 text-yellow-500" />
              ) : (
                <FiMoon className="w-5 h-5 text-gray-600" />
              )}
            </button>

            {/* Cart - Always Visible */}
            <Link
              to="/cart"
              className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
            >
              <FiShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary-light text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* User Menu - Desktop Only */}
            {isAuthenticated ? (
              <div className="hidden md:block relative group">
                <button className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition">
                  <FiUser className="w-5 h-5" />
                  <span>{user?.name}</span>
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all border border-gray-100 dark:border-gray-700">
                  <Link
                    to="/account"
                    className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    My Account
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin/dashboard"
                      className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 font-bold text-primary-light"
                    >
                      Admin Panel
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-red-500"
                  >
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <Link
                to="/auth"
                className="hidden md:block btn-primary text-sm py-2 px-4"
              >
                Login
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300"
            >
              {mobileMenuOpen ? (
                <FiX className="w-6 h-6" />
              ) : (
                <FiMenu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.nav
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="md:hidden py-6 border-t border-gray-100 dark:border-gray-800 space-y-4"
            >
              <div className="flex flex-col space-y-1">
                <Link
                  to="/"
                  className="px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 font-medium transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  to="/products"
                  className="px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 font-medium transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Explore Collection
                </Link>
                <Link
                  to="/about"
                  className="px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 font-medium transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Our Story
                </Link>
              </div>

              <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
                {isAuthenticated ? (
                  <div className="space-y-1">
                    <div className="px-4 py-3 text-sm text-gray-500">Logged in as: <span className="text-gray-900 dark:text-white font-bold">{user?.name}</span></div>
                    <Link
                      to="/account"
                      className="px-4 py-3 rounded-xl flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <FiUser /> My Account
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin/dashboard"
                        className="px-4 py-3 rounded-xl flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 font-bold text-primary-light transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Admin Dashboard
                      </Link>
                    )}
                    <button
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className="w-full text-left px-4 py-3 rounded-xl flex items-center gap-3 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 font-medium transition-colors"
                    >
                      Sign Out
                    </button>
                  </div>
                ) : (
                  <div className="px-4">
                    <Link
                      to="/auth"
                      className="btn-primary w-full flex justify-center py-3"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Login / Create Account
                    </Link>
                  </div>
                )}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
